import React, { useEffect, useState, useContext } from 'react';

import {
  Stack,
  Select,
  Box,
  SelectChangeEvent,
  MenuItem,
  FormControl,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  InputLabel,
  Tooltip,
  useTheme,
} from '@mui/material';
import TimeDisplay from '../TimeDisplay';
import { Link } from 'react-router-dom';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';

import { VoteData } from '../model/types';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import AvatarIcon from '../resource/AvatarIcon';
import { DateTime } from 'luxon';
import HourglassUpIcon from '../resource/HourglassUpIcon';
import HourglassDownIcon from '../resource/HourglassDownIcon';
import PermanenceIcon from '../resource/PermanenceIcon';

type SortOption = 'newest' | 'oldest' | 'largest' | 'smallest';

function VotesList({
  isPost,
  recordID,
  showMenu = true,
  maxItems,
}: {
  isPost: boolean;
  recordID: string;
  showMenu?: boolean;
  maxItems?: number;
}) {
  const [votes, setVotes] = useState<VoteData[] | undefined>();
  const [feedType, setFeedType] = useState('votesBy');
  const [sortOrder, setSortOrder] = useState<SortOption>('newest');
  const [filterType, setFilterType] = useState<string | null>(null);

  const theme = useTheme();
  const getUserRecord =
    useContext<CacheContextType>(CacheContext).getUserRecord;
  const getVotes = useContext<CacheContextType>(CacheContext).allVotes;
  const getPost = useContext<CacheContextType>(CacheContext).getPost;
  const getFollow = useContext<CacheContextType>(CacheContext).getFollow;

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as SortOption);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterType(event.target.value);
  };

  const clearFilter = () => {
    setFilterType(null);
  };

  useEffect(() => {
    if (!recordID) return;
    let snapshotVotes: VoteData[] = getVotes();

    // Filter votes based on criteria
    snapshotVotes = snapshotVotes.filter((vote: VoteData) => {
      if (vote.created_at === undefined) return false;

      if (isPost) {
        return vote.parent_id === recordID;
      } else if (feedType === 'votesFor') {
        // Votes made ON this user's content
        const parentPost = getPost(vote.parent_id);
        const parentFollow = getFollow(vote.parent_id);
        const parent = parentPost || parentFollow;
        return parent?.user_record_id === recordID;
      } else if (feedType === 'votesBy') {
        // Votes cast BY this user
        return vote.user_record_id === recordID;
      }
      return false;
    });

    // Apply filter if set
    if (filterType) {
      switch (filterType) {
        case 'positive':
          snapshotVotes = snapshotVotes.filter((vote) => vote.time_value > 0);
          break;
        case 'negative':
          snapshotVotes = snapshotVotes.filter((vote) => vote.time_value < 0);
          break;
        case 'permanent':
          snapshotVotes = snapshotVotes.filter((vote) => vote.made_permanent);
          break;
        case 'revived':
          snapshotVotes = snapshotVotes.filter((vote) => vote.revived);
          break;
      }
    }

    // Sort votes based on sort order
    snapshotVotes.sort((a, b) => {
      switch (sortOrder) {
        case 'newest':
          return DateTime.fromISO(b.created_at).toSeconds() - DateTime.fromISO(a.created_at).toSeconds();
        case 'oldest':
          return DateTime.fromISO(a.created_at).toSeconds() - DateTime.fromISO(b.created_at).toSeconds();
        case 'largest':
          return Math.abs(b.time_value || 0) - Math.abs(a.time_value || 0);
        case 'smallest':
          return Math.abs(a.time_value || 0) - Math.abs(b.time_value || 0);
        default:
          return 0;
      }
    });

    // Limit number of items if maxItems is specified
    if (maxItems && snapshotVotes.length > maxItems) {
      snapshotVotes = snapshotVotes.slice(0, maxItems);
    }

    setVotes(snapshotVotes);
  }, [recordID, getVotes, feedType, sortOrder, filterType, maxItems, isPost]);

  const handleChange = (event: SelectChangeEvent) => {
    setFeedType(event.target.value as string);
  };

  // Render a message when no votes are available
  if (!recordID || !votes || votes.length === 0)
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        <Typography sx={{ fontFamily: '"VT323", monospace', fontSize: '1.5rem', opacity: 0.4, letterSpacing: 2 }}>
          {isPost ? '[ NO VOTES YET ]' : '[ NO VOTE ACTIVITY ]'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, opacity: 0.6 }}>
          {isPost ? 'Be the first to vote on this post!' : 'No voting activity to display.'}
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        borderRadius: 2,
        p: 2,
        width: '100%',
      }}
    >
      {showMenu && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          {!isPost && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="vote-type-label">Vote Type</InputLabel>
              <Select
                labelId="vote-type-label"
                value={feedType}
                label="Vote Type"
                onChange={handleChange}
              >
                <MenuItem value="votesBy">Votes By User</MenuItem>
                <MenuItem value="votesFor">Votes For User</MenuItem>
              </Select>
            </FormControl>
          )}

          <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="sort-order-label">Sort By</InputLabel>
              <Select
                labelId="sort-order-label"
                value={sortOrder}
                label="Sort By"
                onChange={handleSortChange}
                startAdornment={
                  <SortIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                }
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="largest">Largest</MenuItem>
                <MenuItem value="smallest">Smallest</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="filter-label">Filter</InputLabel>
              <Select
                labelId="filter-label"
                value={filterType || ''}
                label="Filter"
                onChange={handleFilterChange}
                displayEmpty
                startAdornment={
                  <FilterListIcon
                    fontSize="small"
                    sx={{ mr: 1, opacity: 0.7 }}
                  />
                }
              >
                <MenuItem value="">All Votes</MenuItem>
                <MenuItem value="positive">Positive</MenuItem>
                <MenuItem value="negative">Negative</MenuItem>
                <MenuItem value="permanent">Permanent</MenuItem>
                <MenuItem value="revived">Revived Posts</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      )}

      {filterType && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Filtered: ${filterType}`}
            onDelete={clearFilter}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Object</TableCell>
              <TableCell align="right">Date</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {votes.map((vote) => {
              const user = getUserRecord(vote.user_record_id);
              const timeAgo = DateTime.fromISO(vote.created_at).toRelative();

              // Get target object (post or follow)
              const targetObject =
                vote.object_type === 'posts'
                  ? getPost(vote.parent_id)
                  : getFollow(vote.parent_id);

              // Time value formatting with correct sign and display
              const timeValueAbs = Math.abs(vote.time_value || 0);
              const timeValueFormatted = (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {vote.time_value > 0 ? (
                    <HourglassUpIcon
                      style={{
                        width: 16,
                        height: 16,
                        marginRight: 4,
                        color: theme.palette.success.main,
                      }}
                    />
                  ) : (
                    <HourglassDownIcon
                      style={{
                        width: 16,
                        height: 16,
                        marginRight: 4,
                        color: theme.palette.error.main,
                      }}
                    />
                  )}
                  <TimeDisplay timeValue={timeValueAbs * 1000} />
                </Box>
              );

              return (
                <TableRow key={vote.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        <AvatarIcon width="24" height="24" />
                      </Avatar>
                      <Link to={`/${user?.username}`}>
                        {user?.username || vote.user_record_id.slice(0, 8)}
                      </Link>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {vote.made_permanent ? (
                      <Tooltip title="Made permanent">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PermanenceIcon
                            style={{ width: 16, height: 16, marginRight: 4 }}
                          />
                          <Typography variant="body2">Permanent</Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      timeValueFormatted
                    )}
                  </TableCell>
                  <TableCell>
                    {targetObject?.value ? (
                      <Link to={`/${vote.object_type}/${vote.parent_id}`} style={{ textDecoration: 'none' }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 140, color: 'text.primary' }}>
                          {targetObject.value.slice(0, 40)}{targetObject.value.length > 40 ? '…' : ''}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.disabled" noWrap>
                        {vote.object_type === 'posts' ? 'post' : 'follow'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={DateTime.fromISO(vote.created_at).toFormat('yyyy-MM-dd HH:mm:ss')}
                    >
                      <Typography variant="body2">{timeAgo}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {vote.revived && (
                      <Chip size="small" label="Revived" color="success" />
                    )}
                    {vote.killed && (
                      <Chip size="small" label="Killed" color="error" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {votes.length > 0 && maxItems && votes.length === maxItems && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {maxItems} most recent votes
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default VotesList;
